import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingBag, User, Search, Menu, X, ChevronDown } from "lucide-react";
import { LOGO_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";

export default function Header() {
    const { user, logout } = useAuth();
    const { totalQty } = useCart();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const onSearch = (e) => {
        e.preventDefault();
        if (search.trim()) navigate(`/shop?q=${encodeURIComponent(search.trim())}`);
    };

    const links = [
        { to: "/shop", label: "Products" },
        { to: "/research", label: "Research" },
        { to: "/quality", label: "Quality" },
        { to: "/about", label: "About" },
        { to: "/contact", label: "Contact" },
    ];

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? "glass-nav" : "bg-[var(--bg)]"}`}
        >
            {/* Compliance banner — Research Use Only positioning (Stripe-readiness signal) */}
            <div className="bg-[var(--bg-carbon)] border-b border-[var(--brand)] py-1.5">
                <div className="max-w-7xl mx-auto px-6 flex items-center justify-center">
                    <span data-testid="ruo-compliance-banner" className="font-ui text-[0.65rem] tracking-widest text-[var(--brand)] uppercase">
                        Research Use Only · Not for Human or Animal Consumption
                    </span>
                </div>
            </div>
            {/* Promo banner — storewide 30% off */}
            <div className="bg-[var(--brand)] py-1.5">
                <div className="max-w-7xl mx-auto px-6 flex items-center justify-center text-center">
                    <span data-testid="promo-banner" className="font-ui text-[0.68rem] md:text-[0.74rem] tracking-wider text-[var(--bg-carbon)] uppercase font-semibold">
                        30% Off Storewide · Use Code <span className="font-bold tracking-widest">AMINOPLEX30</span> at Checkout
                    </span>
                </div>
            </div>
            {/* top notice */}
            <div className="hidden md:block bg-[var(--text-primary)] py-1.5">
                <div className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-3 text-[var(--bg)]">
                    <span data-testid="free-shipping-banner" className="font-ui text-[0.7rem] tracking-wider">FREE SHIPPING ON ORDERS OVER $200 · RESEARCHER VERIFIED · ≥99% PURITY · INDEPENDENT THIRD-PARTY ANALYSIS</span>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
                    <img src={LOGO_URL} alt="Aminoplex" className="h-11 w-11 object-contain" />
                    <div className="hidden sm:block">
                        <div className="font-display text-xl tracking-[0.08em] text-[var(--text-primary)] leading-none">AMINOPLEX</div>
                        <div className="font-ui text-[0.6rem] uppercase tracking-[0.22em] text-[var(--text-muted)] mt-0.5">Research Compounds</div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {links.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.to.slice(1)}`}
                            className="font-ui font-medium text-[0.92rem] text-[var(--text-primary)] hover:text-[var(--brand)] transition-colors"
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3">
                    <form onSubmit={onSearch} className="hidden md:flex items-center bg-[var(--bg-surface)] rounded-full overflow-hidden focus-within:ring-2 ring-[var(--text-primary)] transition-all">
                        <Search size={14} className="ml-4 text-[var(--text-muted)]" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="header-search-input"
                            placeholder="Search…"
                            className="bg-transparent px-3 py-2 text-sm font-ui outline-none w-32 placeholder:text-[var(--text-muted)]"
                        />
                    </form>

                    {user ? (
                        <div className="relative group">
                            <button data-testid="user-menu-btn" className="btn btn-ghost flex items-center gap-2">
                                <User size={15} />
                                <span className="hidden sm:inline truncate max-w-[100px] font-ui">{user.name?.split(" ")[0] || user.email}</span>
                                <ChevronDown size={12} />
                            </button>
                            <div className="absolute right-0 top-full mt-1 w-56 bg-[var(--bg-carbon)] border border-[var(--border)] rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                                <Link to="/account" data-testid="account-link" className="block px-4 py-3 font-ui text-sm hover:bg-[var(--bg-surface)] hover:text-[var(--brand)] rounded-t-xl">Dashboard</Link>
                                <Link to="/account/orders" className="block px-4 py-3 font-ui text-sm hover:bg-[var(--bg-surface)] hover:text-[var(--brand)]">Order History</Link>
                                <Link to="/account/wishlist" className="block px-4 py-3 font-ui text-sm hover:bg-[var(--bg-surface)] hover:text-[var(--brand)]">Wishlist</Link>
                                {user.role === "admin" && (
                                    <Link to="/admin" data-testid="admin-link" className="block px-4 py-3 font-ui text-sm hover:bg-[var(--bg-surface)] text-[var(--brand)]">Admin</Link>
                                )}
                                <div className="hairline" />
                                <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="block w-full text-left px-4 py-3 font-ui text-sm hover:bg-[var(--bg-surface)] hover:text-[var(--error)] rounded-b-xl">
                                    Log Out
                                </button>
                            </div>
                        </div>
                    ) : (
                        <Link to="/login" data-testid="login-link" className="btn btn-ghost">
                            <User size={15} />
                            <span className="hidden sm:inline">Sign In</span>
                        </Link>
                    )}

                    <Link to="/cart" data-testid="cart-link" className="btn btn-ghost relative">
                        <ShoppingBag size={15} />
                        {totalQty > 0 && (
                            <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-[var(--brand)] text-white text-[0.6rem] font-ui font-semibold px-1.5 py-0.5 leading-none rounded-full min-w-[18px] text-center">
                                {totalQty}
                            </span>
                        )}
                    </Link>

                    <button onClick={() => setOpen(!open)} className="lg:hidden btn btn-ghost" data-testid="mobile-menu-btn">
                        {open ? <X size={16} /> : <Menu size={16} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden bg-[var(--bg-carbon)] border-t border-[var(--border)]">
                    {links.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            onClick={() => setOpen(false)}
                            className="block px-6 py-4 font-ui font-medium text-[var(--text-primary)] border-b border-[var(--border)]"
                        >
                            {l.label}
                        </Link>
                    ))}
                </div>
            )}
        </header>
    );
}
